import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from 'redux/hooks'
import {Divider} from "primereact/divider";
import {Button} from "primereact/button";
import {TabPanel, TabView} from "primereact/tabview";
import {Dialog} from "primereact/dialog";
import {ConfirmDialog} from "primereact/confirmdialog";
import QueuesDialogInfo from "../TrunkDialogInfo";
import QueuesListDialogManagement from "../DialogManagement";
import QueuesListDialogGeneralSettings from "../DialogGeneralSettings";
import {checkPermissions} from "../../../../redux/permissions/permissionsUtils";
import {setTrunkDialogVisible, setTrunkErrorField, setTrunkIds} from "../../../../redux/actions/actionsTrunk";
import PhoneSettings from "../PhoneSettings";
import {
    archiveOrUnArchiveTrunkById,
    createTrunk,
    getDefaultTrunkSettingsList,
    mergeTrunkPhonesWithIds,
    updateTrunk,
} from "../../../../redux/api/apiTrunks";
import {validateTrunkData} from "../../../../redux/validator/validator";
import BlackList from "../BlackList";
import TrunkConfigurationTab from "../TrunkConfigurationTab";

const TAB_GENERAL = 0;
const TAB_TRUNK_CONFIGURATION = 1;
const TAB_PHONES = 2;
const TAB_BLACK_LIST = 3;

const TrunkListDialog = (props: any) => {
    const dispatch = useAppDispatch()
    const trunkData = useAppSelector(state => state.TrunksListReducer.trunkData);
    const trunkPhones = useAppSelector(state => state.TrunksListReducer.trunkIds);
    const dialogVisible = useAppSelector(state => state.TrunksListReducer.trunkDialogVisible);
    const archiveTableToggle = useAppSelector(state => state.TrunksListReducer.archiveTrunkTableToggle);
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectIndex, setSelectIndex] = useState(0);
    const [showDeleteNotification, setShowDeleteNotification] = useState<boolean>(false);
    const [createdSchedule, setCreatedSchedule] = useState<boolean>(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [visible, setVisible] = useState(false);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const trunkErrorFields = useAppSelector(
        (state) => state.TrunksListReducer.errorFields
    );
    const dialerPermissions = checkPermissions(rolesVision, 'Trunks');
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);

    const {
        create: hasTrunksCreatePermission,
        update: hasTrunksUpdatePermission,
        delete: hasTrunksDeletePermission,
    } = dialerPermissions;

    useEffect(() => {
        setActiveIndex(TAB_GENERAL);
    }, [dialogVisible]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (dialogVisible && jwtToken?.jwtToken) {
            dispatch(getDefaultTrunkSettingsList(jwtToken.jwtToken));
        }
    }, [dialogVisible, jwtToken?.jwtToken, dispatch]);

    useEffect(() => {
        if (trunkData?.phones?.length) {
            dispatch(setTrunkIds(trunkData?.phones));
        } else {
            dispatch(setTrunkIds(null));
        }
    }, [dialogVisible]);

    useEffect(() => {
        if (!trunkErrorFields) return;
        if (
            trunkErrorFields.phoneRowErrors &&
            Object.keys(trunkErrorFields.phoneRowErrors).length > 0
        ) {
            setActiveIndex(TAB_PHONES);
            return;
        }
        if (trunkErrorFields.phones) {
            setActiveIndex(TAB_PHONES);
            return;
        }
        if (trunkErrorFields.nameUniq || trunkErrorFields.name) {
            setActiveIndex(TAB_GENERAL);
            return;
        }
        if (trunkErrorFields.branchId) {
            setActiveIndex(TAB_GENERAL);
            return;
        }
        if (trunkErrorFields.defaultTrunkId) {
            setActiveIndex(TAB_TRUNK_CONFIGURATION);
            return;
        }
        if (trunkErrorFields.paiNumber) {
            setActiveIndex(TAB_TRUNK_CONFIGURATION);
        }
    }, [trunkErrorFields]);

    const closeDialogVisible = () => {
        dispatch(setTrunkDialogVisible(false));
        dispatch(setTrunkErrorField(null));
    }

    const updateCampaign = (type: string) => {
        let trunkCopy = {...trunkData};
        const existingPhones = Array.isArray(trunkData?.phones) ? trunkData.phones : [];
        const filteredData = (trunkPhones ?? []).filter(
            (item: { phoneNo: null | string }) =>
                item.phoneNo != null && item.phoneNo !== ""
        );
        const phonesToSave = filteredData.length ? filteredData : existingPhones;

        if (phonesToSave.length) {
            trunkCopy.phones = mergeTrunkPhonesWithIds(phonesToSave, existingPhones);
        }

        const validationErrors = validateTrunkData(trunkCopy);
        if (!validationErrors) {
            const branchIdForRefresh = trunkCopy.branchId ?? null;
            dispatch(updateTrunk(trunkCopy, jwtToken?.jwtToken, branchIdForRefresh ?? null));
            if (type === 'with_close') {
                closeDialogVisible();
            }
        } else {
            dispatch(setTrunkErrorField(validationErrors));
            if (validationErrors.name || validationErrors.branchId) {
                setActiveIndex(TAB_GENERAL);
            } else if (validationErrors.defaultTrunkId) {
                setActiveIndex(TAB_TRUNK_CONFIGURATION);
            } else if (validationErrors.paiNumber) {
                setActiveIndex(TAB_TRUNK_CONFIGURATION);
            } else if (
                validationErrors.phones ||
                validationErrors.limitAttemptsCall ||
                (validationErrors.phoneRowErrors &&
                    Object.keys(validationErrors.phoneRowErrors).length > 0)
            ) {
                setActiveIndex(TAB_PHONES);
            } else if (validationErrors.listOutboundPhones || validationErrors.listInboundPhones) {
                setActiveIndex(TAB_BLACK_LIST);
            }
        }
        setCreatedSchedule(false);
    };

    const createCampaign = (type: string) => {
        let trunkCopy = {...trunkData};
        const filteredPhones = (trunkPhones ?? []).filter(
            (item: { phoneNo: null | string }) =>
                item.phoneNo != null && item.phoneNo !== ""
        );

        if (filteredPhones.length) {
            trunkCopy.phones = filteredPhones;
        }

        const validationErrors = validateTrunkData(trunkCopy);
        if (!validationErrors) {
            dispatch(createTrunk(trunkCopy, jwtToken?.jwtToken));
            if (type === 'with_close') {
                closeDialogVisible();
            }
        } else {
            dispatch(setTrunkErrorField(validationErrors));
            if (validationErrors.name || validationErrors.branchId) {
                setActiveIndex(TAB_GENERAL);
            } else if (validationErrors.defaultTrunkId) {
                setActiveIndex(TAB_TRUNK_CONFIGURATION);
            } else if (validationErrors.paiNumber) {
                setActiveIndex(TAB_TRUNK_CONFIGURATION);
            } else if (
                validationErrors.phones ||
                (validationErrors.phoneRowErrors &&
                    Object.keys(validationErrors.phoneRowErrors).length > 0)
            ) {
                setActiveIndex(TAB_PHONES);
            } else if (validationErrors.listOutboundPhones || validationErrors.listInboundPhones) {
                setActiveIndex(TAB_BLACK_LIST);
            }
        }
        setCreatedSchedule(false);
    };

    const deleteCampaign = () => {
        if (!hasTrunksDeletePermission) {
            return;
        }
        if (jwtToken?.jwtToken && trunkData?.id) {
            dispatch(archiveOrUnArchiveTrunkById(trunkData.id, jwtToken.jwtToken));
        }
        dispatch(setTrunkDialogVisible(false));
        dispatch(setTrunkErrorField(null));
        props.setSelectedRowData?.(null);
    }

    const showDeletionNotification = () => {
        setShowDeleteNotification(true);
    }

    const showSaveChangesNotification = () => {
        dispatch(setTrunkDialogVisible(false));
    }

    const setActiveTabIndex = (e: any) => {
        if ((createdSchedule) && activeIndex === TAB_BLACK_LIST) {
            setVisible(true);
            setSelectIndex(e.index);
        } else {
            setActiveIndex(e.index);
        }
    }

    const closeNotification = () => {
        setVisible(false);
        setActiveIndex(selectIndex);
        setCreatedSchedule(false);
    }

    const saveNewScheduler = () => {
        closeNotification();
    }

    const cancelSaveScheduler = () => {

    }

    const footer = (
        <div className="dialog-footer">
            <Divider/>
            <div className="buttons">
                {archiveTableToggle && hasTrunksDeletePermission && trunkData?.id && (
                    <Button
                        icon={windowWidth > 1248 ? "pi pi-trash" : ''}
                        className="p-button-outlined"
                        label="Видалити"
                        onClick={() => {
                            if (!hasTrunksDeletePermission) {
                                return;
                            }
                            showDeletionNotification();
                        }}
                    />
                )}
                <Button icon={windowWidth > 1248 ? "pi pi-ban" : ''}
                        className="p-button-outlined"
                        label="Відміна"
                        onClick={() => {
                            showSaveChangesNotification()
                        }}
                />
                {hasTrunksCreatePermission && !trunkData?.id && <Button icon={windowWidth > 1248 ? "pi pi-save" : ''}
                                                                        className=""
                                                                        // disabled
                                                                        label="Створити та зберегти"
                                                                        onClick={() => {
                                                                            createCampaign('')
                                                                        }}
                />}
                {hasTrunksUpdatePermission && trunkData?.id && <Button icon={windowWidth > 1248 ? "pi pi-save" : ''}
                                                                       label="Зберегти"
                                                                       // disabled
                                                                       onClick={() => {
                                                                           updateCampaign('')
                                                                       }}
                />}
            </div>
        </div>
    );

    return (
        <Dialog
            header="Налаштування Транка"
            footer={archiveTableToggle ? footer : ''}
            visible={dialogVisible}
            dismissableMask={false}
            closeOnEscape={false}
            draggable={true}
            blockScroll={true}
            position="top"
            className='dialogInfo'
            style={{width: '95vw', maxWidth: "1200px", minHeight: "80vh", zIndex: '1101px !important'}}
            onHide={() => showSaveChangesNotification()}
        >
            {trunkData && <div className="company-info-popup">
                <ConfirmDialog visible={showDeleteNotification} onHide={() => setShowDeleteNotification(false)} message={'Транк буде архівован та деактивован. Архівувати транк?'}
                               icon="pi pi-exclamation-triangle" acceptLabel="Продовжити" rejectLabel="Відмінити" accept={() => deleteCampaign()}/>
                {/*{!companyStateSave && <ConfirmDialog visible={showSaveChangeNotification} onHide={() => setShowSaveChangeNotification(false)} message={saveChangeMessage}*/}
                {/*                                     icon="pi pi-exclamation-triangle" acceptLabel="Зберегти" rejectLabel="Відмінити" reject={() => closeDialogVisible()} accept={() => {*/}
                {/*    updateCampaign('with_close')*/}
                {/*}}/>}*/}
                {/*{companyStateSave && <ConfirmDialog visible={showSaveChangeNotification} onHide={() => setShowSaveChangeNotification(false)} message={saveChangeMessage}*/}
                {/*                                    icon="pi pi-exclamation-triangle" acceptLabel="Зберегти" rejectLabel="Відмінити" reject={() => closeDialogVisible()} accept={() => {*/}
                {/*    createCampaign('with_close')*/}
                {/*}}/>}*/}
                <QueuesDialogInfo/>
                <Divider style={{margin: '0 0 20px 0'}}/>
                {trunkData?.id &&
                    <>
                        <QueuesListDialogManagement/>
                        <Divider style={{margin: '0 0 20px 0'}}/>
                    </>
                }
                <TabView className="user-dialog-info" activeIndex={activeIndex} onTabChange={(e) => setActiveTabIndex(e)}>
                    <TabPanel header="Загальні налаштування">
                        {trunkData && <QueuesListDialogGeneralSettings/>}
                    </TabPanel>
                    <TabPanel header="Конфігурація транка">
                        <TrunkConfigurationTab/>
                    </TabPanel>
                    <TabPanel header="Налаштування номерів">
                        <PhoneSettings/>
                    </TabPanel>
                    <TabPanel header="Black List">
                        <BlackList/>
                    </TabPanel>
                    {/*<TabPanel header="Налаштування ACD">*/}
                    {/*    <ACDSettingsTab/>*/}
                    {/*</TabPanel>*/}
                </TabView>
            </div>}
            <Dialog visible={visible} style={{width: '25vw'}} onHide={() => setVisible(false)}>
                <p className="m-0">
                    Застосувати розклад запуску та завершення кампанії?
                </p>
                <div className="flex justify-content-end mt-4">
                    <Button className="mr-2" onClick={() => saveNewScheduler()}>Так</Button>
                    <Button className="p-button-outlined mr-2" onClick={() => cancelSaveScheduler()}>Ні</Button>
                    <Button className="p-button-outlined" onClick={() => setVisible(false)}>Відміна</Button>
                </div>
            </Dialog>
        </Dialog>
    )
}

export default TrunkListDialog;
